/**
 * @description Component-ScaleSizeInput
 */
import { useTranslation } from 'react-i18next';
import './index.less';

import Input from '@/components/Input';
import { DEFAULT_DECIMALS } from '@/constants/global';
import { TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { updateSimulateError } from '@/features/trade/actions';
import { useTradeStoreTransformed } from '@/features/trade/hooks';
import { useAppDispatch } from '@/hooks';
import { useChainId } from '@/hooks/web3/useChain';
import SymbolSelector from '@/pages/trade/CoinPriceChart/SymbolSelector';
import { parsedEthersError } from '@/utils/error';
import { inputNumChecker } from '@/utils/numberUtil';
import { calcBaseSizeByQuote, calcQuoteSizeByBase } from '@/utils/trade';
import { Context } from '@derivation-tech/context';
import { FC, useCallback } from 'react';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  price: string | undefined;
  disabled?: boolean;
  pair: WrappedPair | undefined;
  sdkContext: Context | undefined;
}
const ScaleSizeInput: FC<IPropTypes> = function ({ disabled, pair, sdkContext, price }) {
  const { t } = useTranslation();
  const chainId = useChainId();
  const dispatch = useAppDispatch();
  const scaleFormState = useTradeStoreTransformed();
  const currentPair = useCurrentPairFromUrl(chainId);
  const updateQuoteAmountByBase = useCallback(
    async (baseAmountStr: string) => {
      if (chainId && sdkContext && pair) {
        try {
          let quoteAmount;
          if (baseAmountStr) {
            quoteAmount = calcQuoteSizeByBase(baseAmountStr, price || 1, pair.isInverse);
          }

          scaleFormState.setTradeQuoteSize(
            quoteAmount
              ? inputNumChecker(
                  WrappedBigNumber.from(quoteAmount).stringValue,
                  pair?.rootInstrument.marginToken.decimals,
                )
              : baseAmountStr,
          );
        } catch (e) {
          const parsedError = parsedEthersError(e);
          if (parsedError) {
            dispatch(
              updateSimulateError({
                type: TRADE_TYPE.SCALE_LIMIT,
                errMessage: parsedError?.errorMsg,
                chainId,
              }),
            );
          }
        }
      }
    },
    [chainId, sdkContext, pair, scaleFormState, price, dispatch],
  );
  const baseInputAmountStrChanged = useCallback(
    (inputAmountStr: string) => {
      if (chainId && sdkContext && pair) {
        scaleFormState.setTradeSize(inputAmountStr);
        updateQuoteAmountByBase(inputAmountStr);
      }
    },
    [chainId, sdkContext, pair, scaleFormState, updateQuoteAmountByBase],
  );
  const updateBaseAmountByQuote = useCallback(
    async (quoteAmountStr: string) => {
      if (chainId && sdkContext && pair) {
        try {
          let baseAmount;
          if (quoteAmountStr) {
            baseAmount = calcBaseSizeByQuote(quoteAmountStr, price || 1, pair.isInverse);
          }
          const checkedBaseAmount = baseAmount
            ? inputNumChecker(
                WrappedBigNumber.from(baseAmount).stringValue,
                pair.rootInstrument.baseToken?.decimals || DEFAULT_DECIMALS,
              )
            : quoteAmountStr;

          scaleFormState.setTradeSize(checkedBaseAmount);
        } catch (e) {
          const parsedError = parsedEthersError(e);
          if (parsedError) {
            dispatch(
              updateSimulateError({
                type: TRADE_TYPE.SCALE_LIMIT,
                errMessage: parsedError?.errorMsg,
                chainId,
              }),
            );
          }
        }
      }
    },
    [chainId, sdkContext, pair, scaleFormState, price, dispatch],
  );
  const quoteInputAmountStrChanged = useCallback(
    (inputAmountStr: string) => {
      if (chainId && sdkContext && pair) {
        scaleFormState.setTradeQuoteSize(inputAmountStr);
        updateBaseAmountByQuote(inputAmountStr);
      }
    },
    [chainId, pair, scaleFormState, sdkContext, updateBaseAmountByQuote],
  );

  return (
    <div className="syn-scale-limit-form-section">
      <div className="syn-scale-limit-form-section-title">{t('common.totalSize')}</div>
      <div className="syn-scale-limit-form-amount">
        <Input
          {...{
            type: 'text',
            pattern: '^[0-9]*[.,]?[0-9]*$',
            inputMode: 'decimal',
            autoComplete: 'off',
            autoCorrect: 'off',
            step: 1e18,
            onKeyDown: (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
          }}
          disabled={disabled}
          value={
            scaleFormState.isUnitInBaseToken ? scaleFormState.tradeSize || '' : scaleFormState.tradeQuoteSize || ''
          }
          placeholder="0.0"
          onChange={(e) =>
            scaleFormState.isUnitInBaseToken
              ? baseInputAmountStrChanged(e.target.value)
              : quoteInputAmountStrChanged(e.target.value)
          }
        />
        <SymbolSelector
          baseToken={currentPair?.rootInstrument.baseToken}
          quoteToken={currentPair?.rootInstrument.quoteToken}
          isBase={scaleFormState.isUnitInBaseToken}
          onChange={scaleFormState.setIsUnitInBaseToken}
        />
      </div>
    </div>
  );
};

export default ScaleSizeInput;
